<script setup>
// Linha unica "rotulo → valor" de atributo personalizado, feita para a
// coluna estreita. Substitui o CustomAttribute legado SO na barra lateral:
// ele trazia faixas de fundo, lixeira sempre visivel e paddings de pagina
// larga que nenhum override de CSS domava. Clicar na linha edita no lugar;
// copiar e apagar so aparecem no hover.
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  attribute: { type: Object, required: true },
  value: { type: [String, Number, Boolean], default: null },
});

const emit = defineEmits(['update', 'remove', 'copy']);

const { t } = useI18n();

const editing = ref(false);
const draft = ref('');
const inputRef = ref(null);

const tipo = computed(() => props.attribute.attribute_display_type);
const chave = computed(() => props.attribute.attribute_key);
const temValor = computed(
  () => props.value !== null && props.value !== undefined && props.value !== ''
);

const valorExibido = computed(() => {
  if (!temValor.value) return '';
  if (tipo.value === 'date') {
    const data = new Date(props.value);
    return Number.isNaN(data.getTime())
      ? String(props.value)
      : data.toLocaleDateString('pt-BR');
  }
  if (tipo.value === 'checkbox') {
    return props.value === true || props.value === 'true'
      ? t('CONVERSATION_SIDEBAR.ATTR_YES')
      : t('CONVERSATION_SIDEBAR.ATTR_NO');
  }
  return String(props.value);
});

const abrirEdicao = async () => {
  // checkbox nao precisa de formulario: o clique ja e a resposta
  if (tipo.value === 'checkbox') {
    const atual = props.value === true || props.value === 'true';
    emit('update', chave.value, !atual);
    return;
  }
  draft.value =
    tipo.value === 'date' && props.value
      ? String(props.value).slice(0, 10)
      : String(props.value ?? '');
  editing.value = true;
  await nextTick();
  inputRef.value?.focus();
};

const salvar = () => {
  if (!editing.value) return;
  editing.value = false;
  const novo = String(draft.value ?? '').trim();
  if (novo === String(props.value ?? '')) return;
  emit('update', chave.value, novo);
};

const cancelar = () => {
  editing.value = false;
};
</script>

<template>
  <!-- edicao no lugar -->
  <div v-if="editing" class="px-2 py-1">
    <span class="mb-1 block text-[11px] text-n-slate-10">
      {{ attribute.attribute_display_name }}
    </span>
    <select
      v-if="tipo === 'list'"
      ref="inputRef"
      v-model="draft"
      class="!mb-0 !h-8 w-full rounded-lg border border-n-brand !text-xs"
      @change="salvar"
      @blur="cancelar"
      @keydown.esc="cancelar"
    >
      <option
        v-for="opcao in attribute.attribute_values || []"
        :key="opcao"
        :value="opcao"
      >
        {{ opcao }}
      </option>
    </select>
    <input
      v-else
      ref="inputRef"
      v-model="draft"
      :type="tipo === 'date' ? 'date' : tipo === 'number' ? 'number' : 'text'"
      class="!mb-0 !h-8 w-full rounded-lg border border-n-brand !text-xs"
      @keydown.enter="salvar"
      @keydown.esc="cancelar"
      @blur="salvar"
    />
  </div>

  <!-- exibicao -->
  <div
    v-else
    class="group/attr flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-n-alpha-1"
    @click="abrirEdicao"
  >
    <span
      class="w-24 shrink-0 truncate text-[11.5px] text-n-slate-10"
      :title="
        attribute.attribute_description || attribute.attribute_display_name
      "
    >
      {{ attribute.attribute_display_name }}
    </span>
    <span
      v-if="temValor"
      class="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-n-slate-12"
    >
      {{ valorExibido }}
    </span>
    <span v-else class="min-w-0 flex-1 truncate text-[12px] text-n-slate-8">
      <b class="font-bold text-n-brand">+</b>
      {{ $t('CUSTOM_ATTRIBUTES.ADD_VALUE') }}
    </span>
    <span v-if="temValor" class="hidden shrink-0 gap-0.5 group-hover/attr:flex">
      <button
        v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
        class="grid size-5 place-content-center rounded-md text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click.stop="emit('copy', valorExibido)"
      >
        <span class="i-lucide-copy size-3" />
      </button>
      <button
        v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        class="grid size-5 place-content-center rounded-md text-n-slate-9 hover:bg-n-ruby-3 hover:text-n-ruby-11"
        @click.stop="emit('remove', chave)"
      >
        <span class="i-lucide-trash-2 size-3" />
      </button>
    </span>
  </div>
</template>
